import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { FlexLayoutModule } from '@angular/flex-layout';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import {
  ComponentBase,
  SharedService,
  BusinessDetailsService,
  ErrorFormComponent,
  NumberOnlyDirective,
  SavePhasesDataType,
} from 'src/app/core';
import {
  REGISTRATION_PHASES_RESPONSE,
  REGITRATIONCYCLEPHASTHREEMODELSTRING,
} from 'src/app/core/model/step-phases.model';
import { RESPONCE } from 'src/app/core';

@Component({
  selector: 'app-tax-setup',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatButtonModule,
    NumberOnlyDirective,
    ErrorFormComponent,
  ],
  templateUrl: './tax-setup.component.html',
  styleUrl: './tax-setup.component.scss',
})
export class TaxSetupComponent extends ComponentBase implements OnInit {
  @Input() titleKey = 'Tax_setup';
  @Input() descriptionKey: string | undefined = 'Tax_setup_description';
  @Input() submitLabelKey = 'Confirm';
  @Input() cancelLabelKey = 'Cancel';
  @Input() registrationCycleLogId: number | undefined;

  /** When false, hide internal action buttons (used when embedded in sidenav). */
  @Input() showActions = true;

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  form!: FormGroup;
  /** Loaded Phase Three data to merge on submit so we don't overwrite other fields. */
  private existingThreeData: Partial<REGITRATIONCYCLEPHASTHREEMODELSTRING> = {};

  constructor(
    private readonly sharedService: SharedService,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    readonly translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.loadExistingData();
    this.form.get('HaveTax')?.valueChanges.subscribe((haveTax) => {
      const taxRateControl = this.form.get('TaxRate');
      const priceIncludeControl = this.form.get('PriceIncludeTax');
      if (haveTax === true) {
        taxRateControl?.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(60),
        ]);
        priceIncludeControl?.setValidators([Validators.required]);
      } else {
        taxRateControl?.clearValidators();
        taxRateControl?.setValue(null);
        priceIncludeControl?.clearValidators();
        priceIncludeControl?.setValue(null);
      }
      taxRateControl?.updateValueAndValidity();
      priceIncludeControl?.updateValueAndValidity();
    });
  }

  get canConfirm(): boolean {
    if (!this.form || this.loading) return false;
    const haveTax = this.form.get('HaveTax')?.value;
    // No: Confirm enabled as soon as user selected No (required choice only)
    if (haveTax === false) {
      return true;
    }
    // Yes: must enter Tax Rate and select Yes/No for "Are your prices inclusive of taxes?"
    if (haveTax === true) {
      return this.form.valid;
    }
    return false;
  }

  private initForm(): void {
    const regIdRaw =
      this.registrationCycleLogId ??
      this.sharedService.getCookie('RegistrationCycleLogId');
    const regId =
      regIdRaw != null && regIdRaw !== ''
        ? typeof regIdRaw === 'number'
          ? regIdRaw
          : +regIdRaw
        : null;

    this.form = new UntypedFormGroup({
      RegistrationCycleLogId: new UntypedFormControl(regId),
      HaveTax: new UntypedFormControl(null, [Validators.required]),
      TaxRate: new UntypedFormControl(null, [
        Validators.max(60),
        Validators.min(1),
      ]),
      PriceIncludeTax: new UntypedFormControl(null),
      SavePhasesDataType: new UntypedFormControl(SavePhasesDataType.Add),
    });
  }

  private loadExistingData(): void {
    const regIdRaw =
      this.registrationCycleLogId ??
      this.sharedService.getCookie('RegistrationCycleLogId');
    if (regIdRaw == null || regIdRaw === '') return;
    const regId =
      typeof regIdRaw === 'number' ? regIdRaw : +regIdRaw;

    this.form.patchValue({ RegistrationCycleLogId: regId });
    this.businessDetailsService
      .getRegisteredPhases({ RegistrationCycleLogId: regId })
      .pipe(take(1))
      .subscribe((res: RESPONCE<REGISTRATION_PHASES_RESPONSE>) => {
        const three = res?.Result?.Three;
        if (three?.ThreeData) {
          const d = three.ThreeData;
          const anyData = d as Record<string, unknown>;
          this.existingThreeData = {
            RegistrationCycleLogId: String(regId),
            BusinessPhoneNumber: d.BusinessPhoneNumber,
            HaveTax: d.HaveTax,
            TaxRate: d.TaxRate != null ? String(d.TaxRate) : undefined,
            PriceIncludeTax: d.PriceIncludeTax,
          };
          this.form.patchValue({
            HaveTax: d.HaveTax,
            TaxRate: d.TaxRate ?? null,
            PriceIncludeTax: d.PriceIncludeTax,
          });
          this.form
            .get('SavePhasesDataType')
            ?.setValue(SavePhasesDataType.Add);
        }
      });
  }

  onCancelClick(): void {
    this.cancel.emit();
  }

  onSubmit(): void {
    if (!this.canConfirm) return;

    const regIdVal = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regIdVal) {
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }

    this.loading = true;
    const formVal = this.form.value;
    const payload: REGITRATIONCYCLEPHASTHREEMODELSTRING = {
      ...this.existingThreeData,
      RegistrationCycleLogId: String(regIdVal),
      HaveTax: formVal.HaveTax,
      TaxRate:
        formVal.HaveTax && formVal.TaxRate != null
          ? String(formVal.TaxRate)
          : undefined,
      PriceIncludeTax: formVal.HaveTax ? formVal.PriceIncludeTax : undefined,
      SavePhasesDataType: formVal.SavePhasesDataType,
    };

    this.businessDetailsService
      .saveTaxSetup(payload)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.loading = false;
          const ok = this.sharedService.checkResponseStatus(
            res.Message,
            res.State
          );
          if (ok) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            this.confirm.emit();
          }
        },
        () => {
          this.loading = false;
        }
      );
  }
}
